/**
 * Design-system foundation — the scales every ds/ component shares.
 *
 * These are deliberately NOT new design decisions. Colour, type and hover come
 * from the tokens in src/index.css via the Square/Mono primitives in
 * ../mono/primitives. What lives here is the *parameter vocabulary* those
 * primitives could not express, because they are class-recipe helpers rather
 * than components: density, alignment, rule style. A helper can hand you a
 * class string; only a component can take `showHeader={false}`.
 *
 * ── The cascade rule (inherited from ../mono/primitives) ───────────────────
 * src/index.css has two hand-written dark rules — `.dark * { border-color:
 * var(--rule) }` (0,1,0) and `.dark h1…h6 { color: var(--text-pri) }` (0,1,1).
 * Tailwind 3 emits no native cascade layers in this build, so what decides is
 * specificity, then order, and both rules come after Tailwind's plain utilities
 * (measured in the running app, 2026-10-01). A plain border-colour class ties
 * with `.dark *` and loses on order; a plain text class on a heading is
 * outweighed. So:
 *   · a NON-default border colour must be an inline style, or
 *   · avoid the border property entirely (ds/ uses inset box-shadow for accents)
 * A `dark:` variant class also wins (it is (0,2,0), and Tailwind writes variant
 * classes last), but it covers dark only.
 * Backgrounds, non-heading text and hover states are unaffected and stay classes.
 */
import type React from 'react';
import type { Tone } from '../mono/primitives';

export type { Tone };

/**
 * Row density. The three steps are the ones the app actually needs: `compact`
 * for dense scan-and-compare grids, `default` for ordinary reading, and
 * `comfortable` for short tables that carry weight on a page (a summary, a
 * verdict). Anything finer is drift.
 */
export type Density = 'compact' | 'default' | 'comfortable';

export const DENSITY_CELL: Record<Density, string> = {
  compact: 'px-3 py-1.5',
  default: 'px-4 py-2.5',
  comfortable: 'px-5 py-4',
};

/** Header cells sit slightly tighter than body cells at every density. */
export const DENSITY_HEAD: Record<Density, string> = {
  compact: 'px-3 py-1.5',
  default: 'px-4 py-2',
  comfortable: 'px-5 py-3',
};

export type Align = 'left' | 'center' | 'right';

export const ALIGN_CLASS: Record<Align, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/** How a table draws its rules. `grid` adds vertical rules for matrix reading. */
export type RuleStyle = 'none' | 'horizontal' | 'grid';

/** The accent bar from the investor deck: a coloured edge, drawn as an inset
    shadow so it never touches the border property the dark cascade overrides. */
export function accentShadow(tone: Tone | undefined, width = 3): React.CSSProperties {
  if (!tone) return {};
  const color =
    tone === 'neutral' ? 'var(--rule-bright)' : `var(--${tone})`;
  return { boxShadow: `inset ${width}px 0 0 0 ${color}` };
}
